import { API_BASE_URL } from './config';
import { apiFetch, escapeHtml, isNetworkError, isValidUrl, markLoaded, renderError } from './helpers';
import type { AiBubbleHistoryPoint, AiBubbleResponse, AiBubbleTechPe } from './types';

interface Band {
    max: number;
    label: string;
    color: string;
}

const BANDS: Band[] = [
    { max: 30, label: 'Cool', color: '#6fa8c8' },
    { max: 50, label: 'Neutral', color: '#7fb884' },
    { max: 70, label: 'Heating Up', color: '#e8b97a' },
    { max: 85, label: 'Elevated Bubble Risk', color: '#e0844a' },
    { max: 100, label: 'Speculative Mania', color: '#c75050' },
];

let aiBubbleChartAbortController: AbortController | null = null;

function bandFor(score: number): Band {
    return BANDS.find(b => score <= b.max) ?? BANDS[BANDS.length - 1];
}

function formatDate(isoDate: string): string {
    const [y, m, d] = isoDate.split('-');
    return `${d}.${m}.${y}`;
}

function formatMonthYear(isoDate: string): string {
    const [y, m] = isoDate.split('-');
    return `${Number(m)}/${y}`;
}

/** How old a reading is in Finnish calendar days — the weekly series can lag by several weeks. */
function ageLabel(isoDate: string): string {
    const [y, m, d] = isoDate.split('-').map(Number);
    const finnishNow = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Helsinki' }));
    const today = Date.UTC(finnishNow.getFullYear(), finnishNow.getMonth(), finnishNow.getDate());
    const days = Math.round((today - Date.UTC(y, m - 1, d)) / 86400000);

    if (days <= 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 14) return `${days} days ago`;
    if (days < 60) return `${Math.round(days / 7)} weeks ago`;
    return `${Math.round(days / 30.44)} months ago`;
}

function techPeMarkup(pe: AiBubbleTechPe | null | undefined): string {
    if (!pe) return '';

    const sourceLink = isValidUrl(pe.sourceUrl)
        ? `<a class="aibubble-pe-source" href="${escapeHtml(pe.sourceUrl)}" target="_blank" rel="noopener noreferrer">${escapeHtml(pe.source)}</a>`
        : '';

    const highs = pe.highs.map(high => `
        <div class="aibubble-pe-high">
            <span class="aibubble-pe-high-label">${escapeHtml(high.label)}</span>
            <span class="aibubble-pe-high-value">${high.value.toFixed(1)}&times;</span>
            <span class="aibubble-pe-high-date">${formatMonthYear(high.date)}</span>
        </div>`).join('');

    return `
        <div class="aibubble-pe">
            <div class="aibubble-pe-head">
                <span class="aibubble-pe-title">Tech sector P/E</span>
                ${sourceLink}
            </div>
            <div class="aibubble-pe-body">
                <div class="aibubble-pe-current">
                    <span class="aibubble-pe-value">${pe.value.toFixed(1)}&times;</span>
                    <span class="aibubble-pe-meta">forward P/E</span>
                </div>
                <div class="aibubble-pe-highs">
                    <span class="aibubble-pe-highs-title">Historical highs</span>
                    ${highs}
                </div>
            </div>
            <p class="aibubble-pe-note">${escapeHtml(pe.index)} share price &divide; the earnings analysts expect over the next 12 months: investors pay ${pe.value.toFixed(1)}&times; one year of forecast profit.</p>
            <p class="aibubble-pe-updated">Weekly reading &middot; updated ${formatDate(pe.date)} (${ageLabel(pe.date)})</p>
        </div>
    `;
}

function changeMarkup(history: AiBubbleHistoryPoint[], current: number, daysBack: number, label: string): string {
    if (history.length === 0) return '';
    const target = new Date(history[history.length - 1].date);
    target.setDate(target.getDate() - daysBack);
    const targetIso = target.toISOString().slice(0, 10);

    let reference: AiBubbleHistoryPoint | null = null;
    for (const point of history) {
        if (point.date <= targetIso) reference = point;
        else break;
    }
    // Fall back to the oldest reading when the history is shorter than the window.
    if (!reference) reference = history[0];

    const delta = current - reference.score;
    if (Math.abs(delta) < 0.05) return `<span class="aibubble-delta aibubble-delta-flat">±0.0 ${label}</span>`;
    const cls = delta > 0 ? 'aibubble-delta-up' : 'aibubble-delta-down';
    const sign = delta > 0 ? '+' : '−';
    return `<span class="aibubble-delta ${cls}">${sign}${Math.abs(delta).toFixed(1)} ${label}</span>`;
}

function drawAiBubbleChart(canvas: HTMLCanvasElement, history: AiBubbleHistoryPoint[]): void {
    if (aiBubbleChartAbortController) aiBubbleChartAbortController.abort();
    aiBubbleChartAbortController = new AbortController();
    const signal = aiBubbleChartAbortController.signal;

    const ctx = canvas.getContext('2d');
    if (!ctx || history.length < 2) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const w = rect.width;
    const h = rect.height;
    const padTop = 12;
    const padBottom = 22;
    const padLeft = 28;
    const padRight = 8;
    const chartW = w - padLeft - padRight;
    const chartH = h - padTop - padBottom;

    const values = history.map(p => p.score);
    const min = 0;
    const max = 100;
    const range = max - min;
    const xStep = chartW / (history.length - 1);
    const yFor = (v: number) => padTop + chartH - ((v - min) / range) * chartH;

    // Band backdrop so the reader can see which regime the line sits in.
    let bandStart = min;
    for (const band of BANDS) {
        const top = yFor(band.max);
        const bottom = yFor(bandStart);
        ctx.fillStyle = `${band.color}14`;
        ctx.fillRect(padLeft, top, chartW, bottom - top);
        bandStart = band.max;
    }

    ctx.strokeStyle = 'rgba(255,255,255,0.05)';
    ctx.lineWidth = 1;
    ctx.font = '10px Inter, sans-serif';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    for (const value of [0, 25, 50, 75, 100]) {
        const y = yFor(value);
        ctx.beginPath();
        ctx.moveTo(padLeft, y);
        ctx.lineTo(w - padRight, y);
        ctx.stroke();
        ctx.fillStyle = '#5e6470';
        ctx.fillText(String(value), padLeft - 6, y);
    }

    const lineColor = bandFor(values[values.length - 1]).color;
    ctx.beginPath();
    ctx.strokeStyle = lineColor;
    ctx.lineWidth = 1.5;
    ctx.lineJoin = 'round';
    for (let i = 0; i < history.length; i++) {
        const x = padLeft + i * xStep;
        const y = yFor(values[i]);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
    }
    ctx.stroke();

    ctx.fillStyle = '#5e6470';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    const labelCount = Math.min(history.length, 6);
    const labelStep = history.length <= labelCount ? 1 : Math.floor(history.length / (labelCount - 1));
    for (let i = 0; i < labelCount; i++) {
        const idx = history.length <= labelCount ? i : Math.min(i * labelStep, history.length - 1);
        const parts = history[idx].date.split('-');
        ctx.fillText(`${parts[2]}.${parts[1]}`, padLeft + idx * xStep, h - 13);
    }

    const lastX = padLeft + (history.length - 1) * xStep;
    const lastY = yFor(values[values.length - 1]);
    ctx.beginPath();
    ctx.arc(lastX, lastY, 3.5, 0, Math.PI * 2);
    ctx.fillStyle = lineColor;
    ctx.fill();

    const tooltip = document.getElementById('aibubble-tooltip');
    if (!tooltip) return;

    canvas.addEventListener('mousemove', (e: MouseEvent) => {
        const canvasRect = canvas.getBoundingClientRect();
        const mx = e.clientX - canvasRect.left;
        const idx = Math.max(0, Math.min(history.length - 1, Math.round((mx - padLeft) / xStep)));
        const point = history[idx];
        tooltip.textContent = `${formatDate(point.date)}  ${point.score.toFixed(1)}`;
        tooltip.style.left = `${padLeft + idx * xStep}px`;
        tooltip.style.top = `${yFor(point.score) - 30}px`;
        tooltip.style.transform = 'translateX(-50%)';
        tooltip.classList.add('visible');
    }, { signal });

    canvas.addEventListener('mouseleave', () => {
        tooltip.classList.remove('visible');
    }, { signal });
}

export async function fetchAiBubble(): Promise<void> {
    const scoreEl = document.getElementById('aibubble-score');
    const componentsEl = document.getElementById('aibubble-components');
    const canvas = document.getElementById('aibubble-chart') as HTMLCanvasElement | null;
    if (!scoreEl) return;

    try {
        const response = await apiFetch(`${API_BASE_URL}/aibubble`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const data: AiBubbleResponse = await response.json();
        const band = bandFor(data.score);
        const history = data.history ?? [];

        scoreEl.innerHTML = `
            <div class="aibubble-top">
                <div class="aibubble-score-block">
                    <div class="aibubble-score-main">
                        <span class="aibubble-value" style="color:${band.color}">${data.score.toFixed(1)}</span>
                        <span class="aibubble-scale">/ 100</span>
                        <span class="aibubble-band" style="color:${band.color};border-color:${band.color}66">${data.label ?? band.label}</span>
                    </div>
                    <div class="aibubble-meter">
                        <div class="aibubble-meter-fill" style="width:${Math.max(0, Math.min(100, data.score))}%;background:${band.color}"></div>
                    </div>
                    <div class="aibubble-changes">
                        ${changeMarkup(history, data.score, 30, '30d')}
                        ${changeMarkup(history, data.score, 365, '1y')}
                    </div>
                </div>
                ${techPeMarkup(data.techPe)}
            </div>
        `;

        if (canvas && history.length > 1) {
            canvas.style.display = '';
            drawAiBubbleChart(canvas, history);
        } else if (canvas) {
            canvas.style.display = 'none';
        }

        if (componentsEl) {
            componentsEl.innerHTML = data.components.map(c => {
                const componentBand = bandFor(c.score);
                const note = c.key === 'valuation'
                    ? '<em class="aibubble-component-note" title="Highest-weighted pillar (25%) — valuation extremes are the strongest historical predictor of drawdowns">most important</em>'
                    : '';
                return `
                    <div class="aibubble-component">
                        <span class="aibubble-component-label">${c.label}${note}</span>
                        <span class="aibubble-component-bar">
                            <span class="aibubble-component-fill" style="width:${Math.max(0, Math.min(100, c.score))}%;background:${componentBand.color}"></span>
                        </span>
                        <span class="aibubble-component-score" style="color:${componentBand.color}">${c.score.toFixed(0)}</span>
                    </div>
                `;
            }).join('');
        }

        markLoaded(scoreEl, 'aibubble');
    } catch (error) {
        renderError(scoreEl, 'aibubble', 'Could not load the AI bubble index.', error);
        if (isNetworkError(error) && scoreEl.dataset.loaded === 'true') return;
        if (canvas) canvas.style.display = 'none';
        if (componentsEl) componentsEl.innerHTML = '';
    }
}
